import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/learn", label: "Learn" },
  { to: "/reviews", label: "Reviews" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenu(false), [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-bone/90 backdrop-blur-md border-b border-sandline" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-1.5">
          <span className={`font-serif text-2xl md:text-3xl leading-none ${solid ? "text-espresso" : "text-bone"}`}>
            17/11
          </span>
          <span className={`overline mt-1 ${solid ? "text-terracotta" : "text-bone/90"}`}>BeautyBarn</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide transition-opacity hover:opacity-60 ${
                solid ? "text-espresso" : "text-bone"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className={`relative p-2 transition-opacity hover:opacity-60 ${solid ? "text-espresso" : "text-bone"}`}
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.6} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 bg-terracotta text-bone text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenu(true)}
            className={`md:hidden p-2 ${solid ? "text-espresso" : "text-bone"}`}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div
            className="fixed inset-0 z-50 bg-espresso text-bone md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center px-5 h-16 border-b border-white/10">
              <span className="font-serif text-2xl">17/11</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenu(false)} className="p-2">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-2">
              {[{ to: "/", label: "Home" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="block font-serif text-4xl py-3 border-b border-white/10"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
